/**
 * JoinTraysPanel — pick 2/3/4 tray segments + a suggested fitting from the
 * manufacturer catalog, then have the backend trim the trays and drop the
 * fitting into the room at the meeting corner. 2 trays produce an elbow /
 * reducer, 3 trays a tee, 4 trays a cross.
 *
 * Lives in its own file (split out of TrayDesigner.jsx in iter_24+1) so each
 * file stays under the team's 700-line guideline.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import FittingIcon from "@/components/trays/FittingIcon";

const FITTING_LABELS = {
  horizontal_elbow_90: "90° elbow",
  horizontal_elbow_45: "45° elbow",
  horizontal_tee: "Tee",
  horizontal_cross: "Cross",
  vertical_elbow_90: "Vertical 90°",
  vertical_elbow_45: "Vertical 45°",
  reducer: "Reducer",
  dropout: "Dropout",
};

function Field({ label, children }) {
  return (
    <div>
      <div className="label-tag mb-1 text-[10px]">{label}</div>
      {children}
    </div>
  );
}

export default function JoinTraysPanel({ segments, projectId, onJoined }) {
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [cId, setCId] = useState("");
  const [dId, setDId] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [joint, setJoint] = useState(null);
  const [pickedSku, setPickedSku] = useState("");
  const [verticalBend, setVerticalBend] = useState(false);
  const [busy, setBusy] = useState(false);

  const joinable = useMemo(
    () => segments.filter((s) => s.kind !== "fitting"),
    [segments],
  );

  const picked = useMemo(
    () => [aId, bId, cId, dId].filter(Boolean),
    [aId, bId, cId, dId],
  );
  const isMulti = picked.length >= 3;
  const allUnique = new Set(picked).size === picked.length;

  const segLabel = (s) => {
    const pts = s.points || [];
    let inTotal = 0;
    for (let i = 1; i < pts.length; i++) {
      inTotal += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    }
    const len = (inTotal / 12).toFixed(1);
    const id = s.id_display || s.id.slice(0, 6);
    const w = s.width_in ? `${s.width_in}″` : "";
    return `${id} · ${s.kind || "run"} · ${len}ft ${w}`;
  };

  // Fetch suggestions whenever the picked set changes (2 / 3 / 4).
  useEffect(() => {
    if (picked.length < 2 || !allUnique) {
      setCandidates([]); setJoint(null); setPickedSku(""); return;
    }
    let cancelled = false;
    (async () => {
      try {
        const endpoint = isMulti
          ? `/projects/${projectId}/trays/fitting/multi-suggest`
          : `/projects/${projectId}/trays/fitting/suggest`;
        const body = isMulti
          ? { seg_ids: picked }
          : { seg_a_id: aId, seg_b_id: bId };
        const { data } = await api.post(endpoint, body);
        if (cancelled) return;
        setCandidates(data.candidates || []);
        setJoint(data.joint || null);
        setPickedSku((data.candidates || [])[0]?.sku || "");
      } catch (e) {
        if (!cancelled) {
          setCandidates([]); setJoint(null); setPickedSku("");
          toast.error(e?.response?.data?.detail || "Suggestion failed");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [picked.join("|"), isMulti, allUnique, projectId]);   // eslint-disable-line

  const applyJoin = async () => {
    if (picked.length < 2 || !pickedSku || !allUnique) return;
    setBusy(true);
    try {
      const endpoint = isMulti
        ? `/projects/${projectId}/trays/fitting/multi-join`
        : `/projects/${projectId}/trays/fitting/join`;
      const body = isMulti
        ? { seg_ids: picked, fitting_sku: pickedSku }
        : { seg_a_id: aId, seg_b_id: bId, fitting_sku: pickedSku,
            vertical_bend: verticalBend };
      const { data } = await api.post(endpoint, body);
      onJoined && onJoined(data.segments || []);
      const fitLabel = FITTING_LABELS[data.fitting?.fitting_type] || "fitting";
      toast.success(`Joined ${picked.length} trays with ${fitLabel} (${pickedSku})`);
      setAId(""); setBId(""); setCId(""); setDId(""); setPickedSku("");
      setCandidates([]); setJoint(null);
      setVerticalBend(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Join failed");
    } finally {
      setBusy(false);
    }
  };

  const aSeg = joinable.find((s) => s.id === aId);
  const bSeg = joinable.find((s) => s.id === bId);

  const TraySelect = ({ value, onChange, testid, label, placeholder = "— choose tray —" }) => (
    <Field label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="input-base text-sm w-full"
              data-testid={testid}>
        <option value="">{placeholder}</option>
        {joinable.map((s) => (
          <option key={s.id} value={s.id}
                  disabled={picked.includes(s.id) && s.id !== value}>
            {segLabel(s)}
          </option>
        ))}
      </select>
    </Field>
  );

  return (
    <div className="mt-4 border border-white/10 bg-[#0a0d12] p-3"
         data-testid="trays-join-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-tag">// Join two, three or four trays with a fitting</div>
          <div className="text-xs text-gray-400 font-mono">
            Pick 2 trays → elbow / reducer.  Add a 3rd → tee.  Add a 4th → cross.
            Trays auto-snap to the fitting's leg points.
          </div>
        </div>
        {joint && (
          <div className="text-[10px] font-mono text-gray-500 text-right">
            {isMulti ? (
              <>
                <div>{picked.length}-way · <span className="text-[#FFCC00]">
                  {joint.fitting_types?.[0]?.replace("horizontal_", "") || "tee"}
                </span></div>
                <div>corner ({joint.corner[0].toFixed(0)}, {joint.corner[1].toFixed(0)})</div>
              </>
            ) : (
              <>
                <div>turn <span className="text-[#FFCC00]">{joint.angle_deg}°</span>
                  {joint.is_vertical ? " · vertical" : " · horizontal"}</div>
                <div>corner ({joint.corner[0].toFixed(0)}, {joint.corner[1].toFixed(0)})</div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <TraySelect value={aId} onChange={setAId} testid="trays-join-a" label="Tray A" />
        <TraySelect value={bId} onChange={setBId} testid="trays-join-b" label="Tray B" />
        <Field label={`Routing suggestions${candidates.length ? ` (${candidates.length}) — pick one` : ""}`}>
          {candidates.length === 0 ? (
            <div className="text-[11px] text-gray-500 font-mono p-2 border border-white/10">
              — pick trays first —
            </div>
          ) : (
            <div className="max-h-44 overflow-y-auto border border-white/10 divide-y divide-white/5"
                 data-testid="trays-join-suggestions">
              {candidates.map((f) => {
                const isPicked = pickedSku === f.sku;
                return (
                  <button key={f.sku}
                          type="button"
                          onClick={() => setPickedSku(f.sku)}
                          className={`w-full text-left p-2 font-mono text-[11px] transition-colors flex items-start gap-2 ${
                            isPicked
                              ? "bg-[#FFCC00]/15 border-l-2 border-[#FFCC00]"
                              : "hover:bg-white/5 border-l-2 border-transparent"
                          }`}
                          data-testid={`trays-join-option-${f.sku}`}>
                    <div className="shrink-0 mt-0.5">
                      <FittingIcon kind={f.fitting_type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={isPicked ? "text-[#FFCC00]" : "text-[#00E5FF]"}>
                          {f.sku}
                        </span>
                        <span className="text-gray-400">${f.list_price_usd}</span>
                      </div>
                      <div className="text-gray-400 truncate">{f.label}</div>
                      <div className="text-[10px] text-gray-500">
                        {f.match_score >= 10
                          ? "same series ✓"
                          : f.match_score >= 5
                            ? "width match"
                            : "generic"}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Field>
      </div>

      {(aId && bId) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <TraySelect value={cId} onChange={setCId} testid="trays-join-c"
                       label="Tray C (optional · tee)" placeholder="— optional —" />
          {cId && (
            <TraySelect value={dId} onChange={setDId} testid="trays-join-d"
                         label="Tray D (optional · cross)" placeholder="— optional —" />
          )}
        </div>
      )}

      {/* Routing hints surfaced from the backend — extension / trim / parallel
          / width-mismatch advice the user should act on BEFORE applying. */}
      {!isMulti && joint?.routing_hints?.length > 0 && (
        <div className="mt-3 border-l-2 border-[#FFCC00] bg-[#FFCC00]/10 pl-3 py-2 text-[11px] font-mono text-[#FFE16B]"
             data-testid="trays-join-routing-hints">
          <div className="text-[#FFCC00] mb-1">// routing suggestions</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {joint.routing_hints.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
        <div className="text-[10px] font-mono text-gray-500">
          {picked.length < 2
            ? "Pick at least two tray segments to see fitting suggestions."
            : !allUnique
              ? "Tray IDs must be unique."
              : candidates.length === 0
                ? "No catalog fitting matches this joint — try different trays."
                : isMulti
                  ? `${picked.length}-way meeting at centroid`
                  : `${aSeg?.width_in || "?"}″ ${aSeg?.material || ""} ⟷ ${bSeg?.width_in || "?"}″ ${bSeg?.material || ""}`}
        </div>
        <div className="flex items-center gap-3">
          {!isMulti && (
            <label className="inline-flex items-center gap-2 text-xs font-mono text-gray-300 cursor-pointer select-none"
                   title="When ON the 3D elbow bends in the Y axis between the two legs (use this for run + drop joins). When OFF the elbow stays flat at the run elevation.">
              <input type="checkbox" checked={verticalBend}
                     onChange={(e) => setVerticalBend(e.target.checked)}
                     className="accent-[#00E5FF]"
                     data-testid="trays-join-vertical-bend" />
              Vertical bend
            </label>
          )}
          <button
            className="btn-primary text-sm py-2 inline-flex items-center gap-2 disabled:opacity-40"
            onClick={applyJoin}
            disabled={busy || picked.length < 2 || !allUnique || !pickedSku}
            data-testid="trays-join-apply">
            {busy ? "Joining…" : `Snap & join${isMulti ? ` ${picked.length}-way` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
